import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  useAuthStore,
  useIsIncludeCompany,
  useIsUserLoaded,
  useSetUser,
  useToken,
} from "@/stores/authStore";
import LoadingFeedback from "@/components/shared/loading/LoadingFeedback";
import { useQueryClient } from "@tanstack/react-query";
import { getAllCompanies, geUserInfo } from "@/api/comman/commanApi";

const ProtectedRoute = ({ children }) => {
  const token = useToken();
  const isIncludeCompany = useIsIncludeCompany();
  const isUserLoaded = useIsUserLoaded();
  const location = useLocation();
  const setUser = useSetUser();
  const queryClient = useQueryClient();

  const { data, error } = geUserInfo();
  const companyId = data?.data?.companyId;
  const { data: companiesData } = getAllCompanies(companyId);

  // logout on error
  useEffect(() => {
    if (error) {
      queryClient.removeQueries({ queryKey: ["user-info"] });
      queryClient.cancelQueries({ queryKey: ["user-info"] });
      useAuthStore.getState().logout();
    }
  }, [error, queryClient]);

  // load user when both endpoints complete
  useEffect(() => {
    if (!token) return;
    if (!data?.data) return;

    const userData = data.data;
    const companyId = userData.companyId;

    if (!companyId) {
      const { email, fullname, isAgency, companyId: userCompanyId, agencyId, userId, policy, roleId, version } = userData;
      setUser({ email, fullname, isAgency, companyId: userCompanyId, agencyId, userId, policy, roleId, version, companies: [] });
      return;
    }

    if (!companiesData?.data) return;

    const { email, fullname, isAgency, companyId: userCompanyId, agencyId, userId, policy, roleId, version } = userData;
    setUser({
      email, fullname, isAgency, companyId: userCompanyId, agencyId, userId, policy, roleId, version,
      companies: companiesData.data.items.map((company) => ({
        label: company?.company?.companyName,
        value: company?.company?.id,
        logo: company?.company?.logo,
      })),
    });
  }, [token, data, companiesData]);

  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }
  if (!isUserLoaded) {
    return <LoadingFeedback />;
  }
  if (!isIncludeCompany && location.pathname !== "/company-registration") {
    return <Navigate to="/company-registration" replace />;
  }
  if (isIncludeCompany && location.pathname === "/company-registration") {
    return <Navigate to="/" />;
  }
  return children;
};

export default ProtectedRoute;
